import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { TASK_CONTEXT_TYPE_LABELS, taskScopeFromLocation } from '@shared/task-contexts';
import type { TaskCard, TaskContextRef, TaskContextType } from '@shared/types/tasks';
import { usePermission } from '@/hooks/usePermission';
import { isSnoozed } from '@/lib/snooze';
import { getPageTitle } from '@/navigation/nav-items';
import { useTaskBoard } from './useTasks';

/** The page's Contexts, plus a label for "this page". Shared by the panel and row badges. */
export function usePageTaskScope() {
  const location = useLocation();
  return useMemo(() => {
    const scope = taskScopeFromLocation(location.pathname, location.search);
    const isPage = scope.primary.type === 'page';
    // Page contexts are named after the page in the navigation (e.g. "Overstock", "Suppliers").
    const title = isPage
      ? getPageTitle(location.pathname) || location.pathname
      : `This ${TASK_CONTEXT_TYPE_LABELS[scope.primary.type].toLowerCase()}`;
    return { ...scope, title };
  }, [location.pathname, location.search]);
}

/** Tasks on this page's Contexts (open and archived), shared between the panel and badges. */
export function usePageTasks(enabled = true) {
  const { scope } = usePageTaskScope();
  return useTaskBoard({ scope, archived: true }, enabled);
}

/** Open (not done, not archived, not snoozed) tasks, optionally only those on one Context. */
export function openTasksFor(tasks: TaskCard[], doneColumnIds: Set<string>, focus?: TaskContextRef | null): TaskCard[] {
  return tasks.filter(
    (t) =>
      !t.archived &&
      !doneColumnIds.has(t.column_id) &&
      !isSnoozed(t.snoozed_until) &&
      (!focus || t.contexts.some((c) => c.type === focus.type && c.id === focus.id))
  );
}

/** Open-task counts per id of one Context type, from the page's tasks (no extra request). */
export function useRowTaskCounts(type: TaskContextType): Map<string, number> {
  const canView = usePermission('TASKS');
  const { board } = usePageTasks(canView);
  return useMemo(() => {
    const counts = new Map<string, number>();
    if (!board) return counts;
    const done = new Set(board.columns.filter((c) => c.is_done).map((c) => c.id));
    for (const task of openTasksFor(board.tasks, done)) {
      for (const c of task.contexts) {
        if (c.type === type) counts.set(c.id, (counts.get(c.id) ?? 0) + 1);
      }
    }
    return counts;
  }, [board, type]);
}

/** The Board has no Tasks panel: it's all tasks already. */
export function tasksPanelHiddenOn(pathname: string): boolean {
  return pathname === '/tasks' || pathname.startsWith('/tasks/');
}
